import { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Stack,
  Divider,
  Box,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  CircularProgress,
  Alert,
  Skeleton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import LockIcon from "@mui/icons-material/Lock";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useNavigate } from "react-router-dom";
import { useCart, useAuthentication, useRestaurant } from "hooks";
import { filter } from "constants";
import { orderService } from "services";
import { OpenHoursTypography, RatingChip } from "components";
import { BasePageDialog } from "./base-page";
import { CART_MAX_QTY } from "constants";

const STEPS = ["Order Summary", "Confirm Order"];

const UNAVAILABLE_OPTIONS = [
  "Remove it from my cart",
  "I'll wait until it's restocked",
  "Please cancel the order",
  "Call me ASAP",
  "Notify me when it's back",
];

const NOTE_MAX = 300;

export function CheckoutDialog({ open, onClose }) {
  const { cart, clearCart, addToCart, removeFromCart } = useCart();
  const { user } = useAuthentication();
  const navigate = useNavigate();

  const { restaurant, loading: restaurantLoading } = useRestaurant(
    open ? cart.restaurantId : null,
  );

  const [activeStep, setActiveStep] = useState(0);
  const [orderType, setOrderType] = useState(null);
  const [unavailableOption, setUnavailableOption] = useState(UNAVAILABLE_OPTIONS[0]);
  const [note, setNote] = useState("");
  const [noteProfane, setNoteProfane] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [placeError, setPlaceError] = useState(null);
  const [validating, setValidating] = useState(false);
  const [cartInvalid, setCartInvalid] = useState(null);
  // Store restaurant data returned from validation so we have prep time
  const [validatedRestaurant, setValidatedRestaurant] = useState(null);

  const serviceStyles =
    restaurant?.serviceStyles?.length
      ? restaurant.serviceStyles
      : cart.restaurantMeta?.serviceStyles?.length
        ? cart.restaurantMeta.serviceStyles
        : ["Dine-in", "Takeaway", "Catering"];

  const lineItems = Object.entries(cart.items).map(([dealId, qty]) => {
    const meta = cart.meta?.[dealId] || {};
    const lineTotal = (meta.price || 0) * qty;
    return { dealId, qty, ...meta, lineTotal };
  });

  const subtotal = lineItems.reduce((sum, item) => sum + item.lineTotal, 0);
  const itemCount = lineItems.reduce((sum, item) => sum + item.qty, 0);

  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setPlaceError(null);
    setValidatedRestaurant(null);

    if (!cart.restaurantId || itemCount === 0) {
      setCartInvalid(null);
      return;
    }

    setValidating(true);
    orderService.validateCartRestaurant(cart.restaurantId).then((result) => {
      setCartInvalid(result.valid ? null : result.reason);
      if (result.valid && result.restaurant) {
        setValidatedRestaurant(result.restaurant);
      }
      setValidating(false);
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && serviceStyles.length > 0) {
      setOrderType((prev) =>
        prev && serviceStyles.includes(prev) ? prev : serviceStyles[0],
      );
    }
  }, [open, serviceStyles.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNoteChange = (val) => {
    const sliced = val.slice(0, NOTE_MAX);
    setNote(sliced);
    setNoteProfane(filter.isProfane(sliced));
  };

  const handleClearCart = () => {
    clearCart();
    setActiveStep(0);
    setNote("");
    setNoteProfane(false);
    setCartInvalid(null);
    onClose();
  };

  const handleClose = () => {
    setActiveStep(0);
    setPlaceError(null);
    onClose();
  };

  const handlePlaceOrder = async () => {
    if (!user) return;
    setPlacing(true);
    setPlaceError(null);

    const r = validatedRestaurant || restaurant;
    const estimatedPrepTime = r?.preparationTime
      ? `${r.preparationTime} ${r.preparationUnit || "minutes"}`
      : null;

    // Enrich restaurantMeta with the vendor's Firebase Auth UID so the order
    // stores restaurantUserId — needed for the customer chat button
    const enrichedCart = {
      ...cart,
      restaurantMeta: {
        ...cart.restaurantMeta,
        userId: r?.userId || cart.restaurantMeta?.userId || null,
      },
    };

    try {
      await orderService.placeOrder({
        cart: enrichedCart,
        orderType,
        unavailableOption,
        note,
        subtotal,
        userId: user.uid,
        estimatedPrepTime,
      });
      clearCart();
      setActiveStep(0);
      setNote("");
      onClose();
      navigate("/profile/orders");
    } catch (err) {
      console.error("Failed to place order:", err);
      setPlaceError("Something went wrong placing your order. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  if (itemCount === 0) {
    return (
      <BasePageDialog open={open} onClose={handleClose} title="Checkout" actions={null} maxWidth="lg">
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 300, gap: 2, textAlign: "center" }}>
          <ShoppingCartIcon sx={{ fontSize: 56, color: "text.disabled" }} />
          <Typography variant="h6">Your cart is empty</Typography>
          <Typography variant="body2" color="text.secondary">Browse deals and add items to get started.</Typography>
          <Button variant="contained" color="primary" onClick={() => { onClose(); navigate("/"); }}>Browse deals</Button>
        </Box>
      </BasePageDialog>
    );
  }

  return (
    <BasePageDialog open={open} onClose={handleClose} title="Checkout" actions={null} maxWidth="lg">
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {validating && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">Verifying restaurant...</Typography>
        </Box>
      )}
      {cartInvalid && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button size="small" color="inherit" onClick={handleClearCart}>Clear cart</Button>}>
          {cartInvalid}
        </Alert>
      )}

      {activeStep === 0 ? (
        <Step1Content
          restaurant={restaurant}
          restaurantLoading={restaurantLoading}
          restaurantId={cart.restaurantId}
          lineItems={lineItems}
          itemCount={itemCount}
          subtotal={subtotal}
          cart={cart}
          addToCart={addToCart}
          removeFromCart={removeFromCart}
          onClearCart={handleClearCart}
          onNext={() => setActiveStep(1)}
          cartInvalid={cartInvalid}
        />
      ) : (
        <Step2Content
          lineItems={lineItems}
          subtotal={subtotal}
          serviceStyles={serviceStyles}
          orderType={orderType}
          setOrderType={setOrderType}
          unavailableOption={unavailableOption}
          setUnavailableOption={setUnavailableOption}
          note={note}
          noteProfane={noteProfane}
          onNoteChange={handleNoteChange}
          onBack={() => setActiveStep(0)}
          onPlaceOrder={handlePlaceOrder}
          placing={placing}
          placeError={placeError}
        />
      )}
    </BasePageDialog>
  );
}

function StickyActions({ children }) {
  return (
    <Box sx={{ position: "sticky", bottom: 0, bgcolor: "background.default", borderTop: 1, borderColor: "divider", py: 2, mt: 2, display: "flex", gap: 2, justifyContent: "space-between", zIndex: 10 }}>
      {children}
    </Box>
  );
}

function Step1Content({ restaurant, restaurantLoading, restaurantId, lineItems, itemCount, subtotal, cart, addToCart, removeFromCart, onClearCart, onNext, cartInvalid }) {
  return (
    <Box sx={{ maxWidth: 680, mx: "auto" }}>
      <Stack spacing={3}>
        {restaurantId && (
          <RestaurantCard restaurant={restaurant} loading={restaurantLoading} fallbackMeta={cart.restaurantMeta} />
        )}
        <Typography variant="subtitle1" fontWeight={600}>Items {itemCount > 0 && `(${itemCount})`}</Typography>
        <Stack spacing={0} divider={<Divider />}>
          {lineItems.map(({ dealId, qty, name, price, imageUrl, lineTotal }) => (
            <Box key={dealId} sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
              <Avatar src={imageUrl} variant="rounded" sx={{ width: 56, height: 56, flexShrink: 0 }} alt={name} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" noWrap fontWeight={500}>{name || "Unknown item"}</Typography>
                <Typography variant="body2" color="text.secondary">R{(price || 0).toFixed(2)} each</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, border: 1, borderColor: "divider", borderRadius: 2, px: 0.5 }}>
                <IconButton size="small" onClick={() => removeFromCart(dealId)} color={qty === 1 ? "error" : "default"}>
                  {qty === 1 ? <DeleteOutlineIcon fontSize="small" /> : <RemoveIcon fontSize="small" />}
                </IconButton>
                <Typography variant="body1" fontWeight={600} sx={{ minWidth: 24, textAlign: "center" }}>{qty}</Typography>
                <IconButton size="small" onClick={() => addToCart(dealId, cart.restaurantId, { name, price, imageUrl })} disabled={qty >= CART_MAX_QTY} color="primary">
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="body1" fontWeight={600} sx={{ flexShrink: 0, minWidth: 72, textAlign: "right" }}>R{lineTotal.toFixed(2)}</Typography>
            </Box>
          ))}
        </Stack>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Subtotal</Typography>
          <Typography variant="h6" fontWeight={700}>R{subtotal.toFixed(2)}</Typography>
        </Box>
      </Stack>
      <StickyActions>
        <Button color="error" variant="outlined" onClick={onClearCart} startIcon={<DeleteOutlineIcon />}>Clear cart</Button>
        <Button variant="contained" color="primary" disabled={!!cartInvalid} onClick={onNext}>Next</Button>
      </StickyActions>
    </Box>
  );
}

function Step2Content({ lineItems, subtotal, serviceStyles, orderType, setOrderType, unavailableOption, setUnavailableOption, note, noteProfane, onNoteChange, onBack, onPlaceOrder, placing, placeError }) {
  return (
    <Box sx={{ maxWidth: 680, mx: "auto" }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600} mb={1.5}>Order type</Typography>
          <ToggleButtonGroup value={orderType} exclusive onChange={(_, val) => val && setOrderType(val)} size="small" sx={{ flexWrap: "wrap", gap: 1 }}>
            {serviceStyles.map((style) => (
              <ToggleButton key={style} value={style} sx={{ borderRadius: "8px !important", border: "1px solid !important", px: 2, textTransform: "none" }}>{style}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Divider />
        <FormControl size="small" fullWidth>
          <InputLabel>If any item is not available</InputLabel>
          <Select value={unavailableOption} label="If any item is not available" onChange={(e) => setUnavailableOption(e.target.value)}>
            {UNAVAILABLE_OPTIONS.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
          </Select>
        </FormControl>
        <Divider />
        <Box>
          <Typography variant="subtitle1" fontWeight={600} mb={1.5}>Payment method</Typography>
          <Paper variant="outlined" sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, borderRadius: 2, cursor: "not-allowed", opacity: 0.85 }}>
            <VolunteerActivismIcon color="action" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>Cash on delivery</Typography>
              <Typography variant="caption" color="text.secondary">Pay when your order arrives</Typography>
            </Box>
            <LockIcon fontSize="small" color="disabled" />
          </Paper>
        </Box>
        <Divider />
        <Box>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>Additional note</Typography>
          <TextField
            placeholder="E.g. no onions, extra sauce, ring doorbell..."
            multiline rows={3} value={note}
            onChange={(e) => onNoteChange(e.target.value)}
            fullWidth error={noteProfane}
            helperText={
              <Box component="span" sx={{ display: "flex", justifyContent: "space-between" }}>
                <span>{noteProfane ? "Note contains inappropriate language." : " "}</span>
                <span style={{ color: note.length >= NOTE_MAX ? "red" : "inherit" }}>{note.length}/{NOTE_MAX}</span>
              </Box>
            }
          />
        </Box>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Total</Typography>
          <Typography variant="h6" fontWeight={700}>R{subtotal.toFixed(2)}</Typography>
        </Box>
        {placeError && <Alert severity="error">{placeError}</Alert>}
      </Stack>
      <StickyActions>
        <Button variant="outlined" color="primary" onClick={onBack} startIcon={<ArrowBackIcon />} disabled={placing}>Back to cart</Button>
        <Button variant="contained" color="primary" onClick={onPlaceOrder} disabled={noteProfane || placing}>
          {placing ? <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><CircularProgress size={16} color="inherit" />Placing order...</Box> : "Place order"}
        </Button>
      </StickyActions>
    </Box>
  );
}

function RestaurantCard({ restaurant, loading, fallbackMeta }) {
  const name = restaurant?.name || fallbackMeta?.name;
  const logoUrl = restaurant?.logoUrl || fallbackMeta?.logoUrl;
  const rating = restaurant?.rating ?? fallbackMeta?.rating;
  const reviewCount = restaurant?.reviewCount ?? fallbackMeta?.reviewCount;
  const phone = restaurant?.phoneNumber;
  const email = restaurant?.email;
  const loc = restaurant?.mainLocation;
  const address = loc?.formattedAddress || [loc?.street, loc?.suburb, loc?.city].filter(Boolean).join(", ") || fallbackMeta?.address || null;

  if (loading && !name) {
    return (
      <Box sx={{ display: "flex", gap: 2, p: 2, borderRadius: 2, bgcolor: "action.hover" }}>
        <Skeleton variant="rounded" width={56} height={56} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="text" width="35%" />
          <Skeleton variant="text" width="65%" />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, p: 2, borderRadius: 2, bgcolor: "action.hover" }}>
      <Avatar src={logoUrl} variant="rounded" sx={{ width: 56, height: 56, flexShrink: 0 }} alt={name}>
        {!logoUrl && name?.charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={700} noWrap>{name || "Restaurant"}</Typography>
        {rating != null && <RatingChip rating={rating} count={reviewCount ?? 0} />}
        {restaurant && <OpenHoursTypography restaurant={restaurant} />}
        {phone && (
          <Typography component="a" href={`tel:${phone}`} variant="body2" color="text.secondary" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, textDecoration: "none", "&:hover": { color: "primary.main", textDecoration: "underline" } }} onClick={(e) => e.stopPropagation()}>
            <CallIcon fontSize="small" />{phone}
          </Typography>
        )}
        {email && (
          <Typography component="a" href={`mailto:${email}`} variant="body2" color="text.secondary" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, textDecoration: "none", "&:hover": { color: "primary.main", textDecoration: "underline" } }} onClick={(e) => e.stopPropagation()}>
            <EmailIcon fontSize="small" />{email}
          </Typography>
        )}
        {address && (
          <Typography variant="body2" color="text.secondary" sx={{ display: "inline-flex", alignItems: "flex-start", gap: 0.5 }}>
            <LocationOnIcon fontSize="small" sx={{ flexShrink: 0, mt: "2px" }} />{address}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
